import { useEffect, useState, useCallback } from "react";
import { Radio, ExternalLink, TrendingUp, Bookmark, BookmarkCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { InkWeather } from "../components/InkWeather";
import { InkMarkets } from "../components/InkMarkets";
import { AffiliateCard } from "../components/AffiliateCard";

const TABS = ["Latest", "Tech", "AI", "Science", "Art & Design", "Gaming", "Business", "Innovation"];
const BM_KEY = "pti_wire_bookmarks";

function readLocalBookmarks() {
  try { return JSON.parse(localStorage.getItem(BM_KEY) || "[]"); } catch { return []; }
}

export default function InkWire() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Latest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trending, setTrending] = useState([]);
  const [bookmarks, setBookmarks] = useState(readLocalBookmarks());
  const [showSaved, setShowSaved] = useState(false);
  const [affInkwire, setAffInkwire] = useState(null);

  useEffect(() => {
    api.get("/affiliate?placement=inkwire&rotate=true&ab=true").then((r) => setAffInkwire(r.data[0] || null)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/inkwire?tab=${encodeURIComponent(tab)}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    const cat = tab === "Latest" ? "" : `&category=${encodeURIComponent(tab)}`;
    api.get(`/inkwire/trending?limit=10${cat}`).then((r) => setTrending(r.data || [])).catch(() => {});
  }, [tab]);

  // Load bookmarks: server (synced) when logged in, else localStorage. Merge local -> server on login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user) {
        try {
          const local = readLocalBookmarks();
          if (local.length) {
            await Promise.all(local.map((b) => api.post("/bookmarks", b).catch(() => {})));
            localStorage.removeItem(BM_KEY);
          }
          const { data } = await api.get("/bookmarks");
          if (!cancelled) setBookmarks(data || []);
        } catch { /* keep current */ }
      } else {
        setBookmarks(readLocalBookmarks());
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const isSaved = useCallback((link) => bookmarks.some((b) => b.link === link), [bookmarks]);

  const toggleBookmark = useCallback((it) => {
    const exists = bookmarks.some((b) => b.link === it.link);
    const entry = {
      link: it.link, title: it.title, source: it.source, category: it.category,
      image: it.image || "", excerpt: it.excerpt || "", published_at: it.published_at || "",
    };
    const next = exists ? bookmarks.filter((b) => b.link !== it.link) : [entry, ...bookmarks];
    setBookmarks(next);
    if (user) {
      if (exists) api.delete(`/bookmarks?link=${encodeURIComponent(it.link)}`).catch(() => {});
      else api.post("/bookmarks", entry).catch(() => {});
    } else {
      localStorage.setItem(BM_KEY, JSON.stringify(next));
    }
  }, [bookmarks, user]);

  const recordRead = useCallback((it) => {
    api.post("/inkwire/read", {
      link: it.link, title: it.title, source: it.source, category: it.category, image: it.image || "",
    }).catch(() => {});
  }, []);

  const fmt = (d) => { try { return new Date(d).toLocaleString("en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  const source = showSaved ? bookmarks : items;
  const featured = showSaved ? null : (items.filter((i) => i.featured || i.pinned)[0] || items[0]);
  const rest = showSaved ? bookmarks : items.filter((i) => i.id !== featured?.id);

  return (
    <div data-testid="inkwire-page">
      {/* Trending ticker */}
      {trending.length > 0 && (
        <div className="bg-pti-black text-white overflow-hidden pti-marquee-wrap border-b border-neutral-800" data-testid="inkwire-trending-ticker">
          <div className="pti-container flex items-center gap-4 py-2">
            <span className="flex items-center gap-1.5 text-pti-red font-black uppercase text-xs tracking-widest shrink-0">
              <TrendingUp size={15} /> Most Read{tab !== "Latest" ? ` · ${tab}` : ""}
            </span>
            <div className="overflow-hidden flex-1">
              <div className="pti-marquee-track">
                {[...trending, ...trending].map((t, i) => (
                  <a key={`${t.link}-${i}`} href={t.link} target="_blank" rel="noopener noreferrer" onClick={() => recordRead(t)}
                    data-testid={`trending-item-${i}`}
                    className="inline-flex items-center gap-2 mr-8 text-sm text-neutral-200 hover:text-pti-red transition-colors">
                    <span className="text-pti-red font-bold">{(i % trending.length) + 1}.</span>
                    <span className="font-semibold">{t.title}</span>
                    <span className="text-neutral-500 text-xs">· {t.source}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pti-container py-10">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-pti-black pb-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-black flex items-center gap-3"><Radio className="text-pti-red" /> InkWire</h1>
            <p className="text-gray-600 mt-1">Curated by PictureThisInk · headlines link to the original publisher.</p>
          </div>
          <span className="flex items-center gap-2 bg-pti-black text-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-pti-red animate-pulse" aria-hidden="true" /> Live Feed
          </span>
        </header>

        <section className="mt-8" data-testid="inkwire-newsroom-tools">
          <div className="flex items-end justify-between border-b-2 border-pti-black pb-2">
            <h2 className="font-display text-2xl font-bold">Newsroom Tools</h2>
            <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Live</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <InkWeather />
            <InkMarkets />
          </div>
        </section>

        {affInkwire && (
          <div className="mt-8 max-w-3xl" data-testid="inkwire-sponsor">
            <AffiliateCard ad={affInkwire} placement="inkwire" className="border-t-4 border-t-pti-red" />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-8 pb-2" role="tablist">
          <button role="tab" aria-selected={!showSaved} onClick={() => setShowSaved(false)} data-testid="inkwire-view-feed"
            className={`whitespace-nowrap px-4 py-1.5 text-sm font-bold border transition-colors ${!showSaved ? "bg-pti-black text-white border-pti-black" : "border-gray-300 text-gray-600 hover:border-pti-black"}`}>
            Live Wire
          </button>
          <button role="tab" aria-selected={showSaved} onClick={() => setShowSaved(true)} data-testid="inkwire-view-saved"
            className={`whitespace-nowrap px-4 py-1.5 text-sm font-bold border transition-colors flex items-center gap-1.5 ${showSaved ? "bg-pti-red text-white border-pti-red" : "border-gray-300 text-gray-600 hover:border-pti-black"}`}>
            <Bookmark size={14} /> Reading List ({bookmarks.length})
          </button>
          <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />
          {!showSaved && TABS.map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`inkwire-tab-${t.replace(/\W/g, "")}`}
              className={`whitespace-nowrap px-4 py-1.5 text-sm font-bold border transition-colors ${tab === t ? "bg-pti-red text-white border-pti-red" : "border-gray-300 text-gray-600 hover:border-pti-black"}`}>
              {t}
            </button>
          ))}
        </div>

        {showSaved && !user && bookmarks.length > 0 && (
          <div className="mt-4 border-l-4 border-pti-red bg-red-50 px-4 py-3 text-sm" data-testid="inkwire-sync-hint">
            <a href="/login" className="font-bold text-pti-red hover:underline">Sign in</a> to sync your reading list across all your devices.
          </div>
        )}

        {showSaved ? (
          bookmarks.length === 0 ? (
            <div className="py-20 text-center text-gray-400" data-testid="inkwire-saved-empty">
              <Bookmark size={40} className="mx-auto mb-3 opacity-40" />
              <p>Your reading list is empty. Tap the bookmark icon on any story to save it here.</p>
            </div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="inkwire-saved-list">
              {bookmarks.map((it) => (
                <div key={it.link} className="group relative border border-gray-200 hover:border-pti-black transition-colors" data-testid={`saved-item-${it.link}`}>
                  <a href={it.link} target="_blank" rel="noopener noreferrer" onClick={() => recordRead(it)} className="block">
                    {it.image && <div className="aspect-video overflow-hidden bg-gray-100"><img src={it.image} alt="" className="w-full h-full object-cover" /></div>}
                    <div className="p-4">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-pti-red">{it.category} · {it.source}</div>
                      <h3 className="text-sm font-semibold leading-snug mt-1 group-hover:text-pti-red transition-colors line-clamp-3">{it.title}</h3>
                    </div>
                  </a>
                  <button onClick={() => toggleBookmark(it)} data-testid={`saved-remove-${it.link}`} aria-label="Remove from reading list"
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur p-1.5 text-pti-red hover:bg-white shadow">
                    <BookmarkCheck size={16} />
                  </button>
                </div>
              ))}
            </div>
          )
        ) : loading ? <p className="py-20 text-center text-gray-400">Loading the wire…</p> : items.length === 0 ? (
          <p className="py-20 text-center text-gray-400">No stories in this category yet.</p>
        ) : (
          <div className="mt-8 grid lg:grid-cols-3 gap-8">
            {featured && (
              <div className="lg:col-span-2 group relative" data-testid={`inkwire-item-${featured.id}`}>
                <a href={featured.link} target="_blank" rel="noopener noreferrer" onClick={() => recordRead(featured)} className="block">
                  {featured.image && <div className="aspect-video overflow-hidden bg-gray-100"><img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold"><span className="text-pti-red">{featured.category}</span><span className="text-gray-400">· {featured.source}</span></div>
                    <h2 className="font-display text-3xl font-bold mt-1 group-hover:text-pti-red transition-colors flex items-start gap-2">{featured.title} <ExternalLink size={18} className="mt-2 opacity-40 shrink-0" /></h2>
                    <p className="text-gray-600 mt-2 line-clamp-2">{featured.excerpt}</p>
                    {featured.why_it_matters && <div className="mt-3 border-l-4 border-pti-red pl-3 text-sm"><strong>Why it matters (PictureThisInk):</strong> {featured.why_it_matters}</div>}
                    <span className="text-xs text-gray-400 mt-2 block">{fmt(featured.published_at)}</span>
                  </div>
                </a>
                <button onClick={() => toggleBookmark(featured)} data-testid={`bookmark-${featured.id}`} aria-label={isSaved(featured.link) ? "Remove from reading list" : "Save to reading list"}
                  className={`absolute top-3 right-3 backdrop-blur p-2 shadow transition-colors ${isSaved(featured.link) ? "bg-pti-red text-white" : "bg-white/90 text-pti-black hover:text-pti-red"}`}>
                  {isSaved(featured.link) ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
                </button>
              </div>
            )}
            <div className="divide-y divide-gray-200">
              {rest.slice(0, 12).map((it) => (
                <div key={it.id} className="flex gap-3 py-3 group items-start" data-testid={`inkwire-item-${it.id}`}>
                  <a href={it.link} target="_blank" rel="noopener noreferrer" onClick={() => recordRead(it)} className="flex gap-3 flex-1 min-w-0">
                    {it.image && <img src={it.image} alt="" className="w-20 h-16 object-cover bg-gray-100 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-pti-red">{it.category} · {it.source}</div>
                      <h3 className="text-sm font-semibold leading-snug group-hover:text-pti-red transition-colors line-clamp-2">{it.title}</h3>
                      <span className="text-[11px] text-gray-400">{fmt(it.published_at)}</span>
                    </div>
                  </a>
                  <button onClick={() => toggleBookmark(it)} data-testid={`bookmark-${it.id}`} aria-label={isSaved(it.link) ? "Remove from reading list" : "Save to reading list"}
                    className={`shrink-0 p-1.5 transition-colors ${isSaved(it.link) ? "text-pti-red" : "text-gray-300 hover:text-pti-red"}`}>
                    {isSaved(it.link) ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
